import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ShieldAlert, ShieldCheck, Send, Plus } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { fmtDate } from "../../lib/status";

const TRIGGER_LABELS = {
  expired_document: "Expired required document",
  critical_damage: "Serious vehicle damage",
  unsafe_maintenance: "Marked unsafe to operate",
  critical_inspection_finding: "Critical inspection finding",
  odometer_anomaly: "Odometer integrity anomaly",
  identity_integrity: "Vehicle identity issue",
  manual: "Reported issue",
};

function ResolveForm({ hold, onResolved }) {
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/enhanced-safety/holds/${hold.id}/resolve`, {
        resolution_type: "manual_review", notes,
      });
      toast.success("Safety Hold resolved.");
      onResolved(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg border border-red-300 bg-white p-3 space-y-2" data-testid="resolve-hold-form">
      <label className="block text-xs font-semibold uppercase tracking-wide text-red-800">
        Resolution notes
      </label>
      <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
                data-testid="resolve-hold-notes"
                placeholder="What was done to resolve this? (repair, document renewal, correction, evidence...)"
                className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm" />
      <button onClick={submit} disabled={busy || notes.trim().length < 5}
              data-testid="submit-resolve-hold-btn"
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-red-700 text-white text-sm font-medium disabled:opacity-40">
        <Send className="w-3.5 h-3.5" /> Submit Resolution
      </button>
    </div>
  );
}

function ReportIssueForm({ vehicleId, onOpened, onCancel }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/enhanced-safety/vehicles/${vehicleId}/holds`, {
        trigger_type: "manual", reason,
      });
      toast.success("Safety Hold placed on this vehicle.");
      onOpened(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 space-y-2" data-testid="report-safety-issue-form">
      <label className="block text-xs font-semibold uppercase tracking-wide text-amber-900">
        Describe the serious safety concern
      </label>
      <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
                data-testid="report-issue-reason"
                placeholder="e.g. Brakes felt unsafe during walkaround, steering issue reported by renter..."
                className="w-full px-3 py-2 rounded-lg border border-amber-300 text-sm" />
      <div className="flex gap-2">
        <button onClick={submit} disabled={busy || reason.trim().length < 5}
                data-testid="submit-report-issue-btn"
                className="px-3 py-1.5 rounded-lg bg-red-700 text-white text-sm font-medium disabled:opacity-40">
          Place Safety Hold
        </button>
        <button onClick={onCancel} className="px-3 py-1.5 rounded-lg border border-slate-300 text-sm">Cancel</button>
      </div>
    </div>
  );
}

/**
 * Vehicle detail's Safety Hold surface: shows the active hold (if any)
 * with affected bookings + Resolve Issue, or (when Enhanced Safety is
 * active and no hold is open) a "Report Safety Issue" action for
 * judgment-based cases. Renders nothing when Enhanced Safety isn't active
 * for this business at all.
 */
export default function SafetyHoldPanel({ vehicleId, canReport = false, onChanged }) {
  const [enabled, setEnabled] = useState(null); // null = loading
  const [openHold, setOpenHold] = useState(null);
  const [affected, setAffected] = useState([]);
  const [showReport, setShowReport] = useState(false);
  const [showResolve, setShowResolve] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data: settings } = await api.get("/enhanced-safety/settings");
      // A pending deactivation request is a sub-state layered ON TOP of
      // "active", not a replacement for it - the program stays fully in
      // force (new issues can still be reported) until ConfirmPH actually
      // approves the request.
      setEnabled(settings.state === "active" || settings.state === "deactivation_requested");
    } catch (_) { setEnabled(false); }
    try {
      const { data: holds } = await api.get(`/enhanced-safety/vehicles/${vehicleId}/holds`);
      const open = (holds || []).find((h) => h.status === "open") || null;
      setOpenHold(open);
      if (open) {
        const { data: aff } = await api.get(`/enhanced-safety/holds/${open.id}/affected-bookings`);
        setAffected(aff || []);
      } else {
        setAffected([]);
      }
    } catch (_) { /* tolerate */ }
  }, [vehicleId]);

  useEffect(() => { load(); }, [load]);

  if (enabled === null) return null;
  if (!enabled && !openHold) return null; // nothing to show, feature not in use

  if (!openHold) {
    return (
      <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid="safety-hold-panel-clear">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-sm text-emerald-800">
            <ShieldCheck className="w-4 h-4" /> No active ConfirmPH Safety Hold
          </div>
          {canReport && !showReport && (
            <button onClick={() => setShowReport(true)} data-testid="open-report-issue-btn"
                    className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 rounded-lg border border-amber-300 bg-amber-50 text-amber-900">
              <Plus className="w-3.5 h-3.5" /> Report Safety Issue
            </button>
          )}
        </div>
        {showReport && (
          <div className="mt-3">
            <ReportIssueForm vehicleId={vehicleId} onCancel={() => setShowReport(false)}
                              onOpened={() => { setShowReport(false); load(); onChanged?.(); }} />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="rounded-lg border-2 border-red-400 bg-red-50 p-4 space-y-3" data-testid="safety-hold-banner">
      <div className="flex items-start gap-2">
        <ShieldAlert className="w-5 h-5 text-red-700 flex-shrink-0 mt-0.5" />
        <div>
          <div className="font-display text-base font-semibold text-red-900">CONFIRMPH SAFETY HOLD</div>
          <p className="text-sm text-red-900 mt-0.5">
            This vehicle cannot complete a ConfirmPH rental handover until the issue below is resolved.
          </p>
        </div>
      </div>
      <div className="rounded-lg bg-white border border-red-200 p-3 text-sm">
        <div className="font-medium text-slate-900">{TRIGGER_LABELS[openHold.trigger_type] || openHold.trigger_type}</div>
        <div className="text-slate-600 mt-0.5">{openHold.reason}</div>
        <div className="text-xs text-slate-500 mt-1">Opened {fmtDate(openHold.opened_at)}</div>
      </div>

      {affected.length > 0 && (
        <div data-testid="affected-bookings-list">
          <div className="text-xs font-semibold uppercase tracking-wide text-red-800 mb-1">
            {affected.length} upcoming booking{affected.length === 1 ? "" : "s"} affected
          </div>
          <div className="space-y-1">
            {affected.map((a) => (
              <Link key={a.booking_id} to={`/app/bookings/${a.booking_id}`}
                    className="block rounded-lg bg-white border border-red-100 p-2 text-sm hover:bg-red-50">
                {a.booking?.customer_name || "Customer"} · {a.booking?.booking_reference}
                <span className="text-slate-500"> · {fmtDate(a.booking?.start_datetime)}</span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {canReport && !showResolve && (
        <button onClick={() => setShowResolve(true)} data-testid="open-resolve-hold-btn"
                className="px-3 py-1.5 rounded-lg bg-red-700 text-white text-sm font-medium">
          Resolve Issue
        </button>
      )}
      {showResolve && (
        <ResolveForm hold={openHold} onResolved={() => { setShowResolve(false); load(); onChanged?.(); }} />
      )}
    </div>
  );
}
